import type { ReactNode } from 'react';
import type { MarketFact } from '~/features/shared/market-content';
import {
  MarketActionRow,
  MarketContentPanel,
  MarketFactGrid,
  MarketStatusPill,
} from '~/features/shared/market-content';
import { b2bSideCopy } from '../../b2bSideCopy';
import { ContractReadyNotice } from '../../contracts/ContractReadyNotice';
import type { B2BAvailableAction, B2BOpportunity, B2BTurn } from '../types';
import {
  formatCurrency,
  formatDate,
  formatHourlyRate,
  formatWorkLocation,
  isListingFilled,
} from '../utils';
import { B2BNegotiationHistory } from './B2BNegotiationHistory';
import { ListingFilledNotice } from './ListingFilledNotice';
import { ResponseProvidersSection } from './ResponseProvidersSection';

type Props = {
  opportunity: B2BOpportunity;
  onAccept: () => void;
  onCounter: (responseId: number, options?: { startFirstNegotiation: boolean }) => void;
  onWithdraw: () => void;
  onApply?: () => void;
  actionsDisabled?: boolean;
  /** [MCV-1889] DD22: below 1360px the actions are pinned to the bottom of the screen. */
  pinActions?: boolean;
  identityControls?: ReactNode;
};

type TurnStatus = Parameters<typeof MarketStatusPill>[0]['status'];
type TurnDisplay = { label: string; status: TurnStatus; pulse: boolean } | null;

const getHeaderTurnDisplay = (turn: B2BTurn): TurnDisplay => {
  if (turn === 'YOUR_TURN') {
    return { label: 'Your Turn', pulse: true, status: 'yourTurn' };
  }
  if (turn === 'THEIR_TURN') {
    return { label: 'Their Turn', pulse: false, status: 'theirTurn' };
  }
  return null;
};

const hasAction = (actions: B2BAvailableAction[], action: B2BAvailableAction) =>
  actions.includes(action);

export const OpportunityDetail = ({
  opportunity,
  onAccept,
  onCounter,
  onWithdraw,
  onApply,
  actionsDisabled,
  pinActions,
  identityControls,
}: Props) => {
  const {
    listing,
    listingOwner,
    latestNegotiation,
    negotiationSummaries,
    responseProviders = [],
  } = opportunity;
  const turnDisplay = getHeaderTurnDisplay(opportunity.turn);
  const copy = b2bSideCopy(opportunity.listingSide);
  // [MCV-1889] A response closed because its listing filled offers no actions.
  const listingFilled = isListingFilled(opportunity);
  const ownerNoun = copy.ownerNoun.charAt(0).toUpperCase() + copy.ownerNoun.slice(1);

  const showAccept = !listingFilled && hasAction(opportunity.availableActions, 'accept');
  const showCounter = !listingFilled && hasAction(opportunity.availableActions, 'counter');
  const showApply =
    opportunity.status === 'MESSAGED' && latestNegotiation === null && showCounter && onApply;
  const showWithdraw = !listingFilled && hasAction(opportunity.availableActions, 'withdraw');
  const showActions = showAccept || showCounter || showWithdraw;
  const counterLabel = latestNegotiation ? 'Counter' : 'Negotiate';
  const pendingAcceptanceMessage =
    opportunity.status !== 'PENDING_ACCEPTANCE'
      ? null
      : opportunity.turn === 'YOUR_TURN'
        ? `${ownerNoun} accepted the current offer. Review and accept it to complete the agreement.`
        : `You accepted the current offer. ${ownerNoun} must confirm before the agreement is complete.`;

  const listingFacts = (
    [
      listing.specialtyName ? { label: 'Specialty', value: listing.specialtyName } : null,
      { label: 'States', value: listing.stateCodes.join(', ') || '—' },
      { label: 'Work Location', value: formatWorkLocation(listing.workLocation) },
      listing.numOfFtes != null ? { label: 'FTEs', value: String(listing.numOfFtes) } : null,
      listing.pricePerHour != null
        ? { label: 'Rate', value: formatHourlyRate(listing.pricePerHour) }
        : null,
      listing.totalContractValue != null
        ? {
            label: 'Contract Value',
            value: formatCurrency(listing.totalContractValue),
            highlight: true,
          }
        : null,
      listing.startDate || listing.endDate
        ? {
            label: 'Duration',
            value:
              listing.startDate && listing.endDate
                ? `${formatDate(listing.startDate)} – ${formatDate(listing.endDate)}`
                : listing.startDate
                  ? `${formatDate(listing.startDate)}+`
                  : `Until ${formatDate(listing.endDate)}`,
          }
        : null,
    ] as Array<MarketFact | null>
  ).filter((fact): fact is MarketFact => fact !== null);

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      dataCy="b2b-opportunity-detail"
      variant="scroll"
    >
      {/* Listing Summary */}
      <div className="shrink-0 border-b border-grey-900 p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <MarketStatusPill status="responded" label="B2B" />
            <h2 className="sub-heading-sb text-content-primary">{listing.listingTitle}</h2>
          </div>
          {turnDisplay && (
            <MarketStatusPill
              status={turnDisplay.status}
              label={turnDisplay.label}
              pulse={turnDisplay.pulse}
              cyTag="turn-badge"
            />
          )}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 text-[13px] text-content-secondary">
          <span>{copy.listedByLabel}</span>
          {listingOwner.masked ? (
            <span className="text-xs font-semibold text-grey-500">ID</span>
          ) : (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-primary-t-50 text-[10px] font-bold text-white">
              {listingOwner.displayName.charAt(0)}
            </span>
          )}
          <span className="font-medium text-content-primary">{listingOwner.displayName}</span>
        </div>

        <MarketFactGrid facts={listingFacts} />
        {listingFilled && <ListingFilledNotice className="mt-5" />}
        {pendingAcceptanceMessage && !listingFilled && (
          <div
            className="mt-5 rounded-lg border border-primary-t-100 bg-primary-t-900/40 p-4 text-left"
            data-cy="b2b-pending-acceptance-notice"
          >
            <h3 className="text-sm font-semibold text-content-primary">Pending acceptance</h3>
            <p className="mt-1 text-sm text-content-secondary">{pendingAcceptanceMessage}</p>
          </div>
        )}
        {/* [MCV-1954] Both parties accepted: the contract is waiting on the Contracts page. */}
        {opportunity.status === 'ACCEPTED' && (
          <div className="mt-5">
            <ContractReadyNotice />
          </div>
        )}
        {/* Action buttons sit left-aligned under the facts, above Identity access. */}
        {showActions && (
          <MarketActionRow
            align="start"
            className="mt-5"
            pinned={pinActions}
            actions={[
              ...(showApply
                ? [
                    {
                      label: 'Apply',
                      cyTag: 'btn-apply',
                      onClick: onApply,
                      disabled: actionsDisabled,
                      colorVariant: 'primary' as const,
                    },
                  ]
                : []),
              ...(showAccept
                ? [
                    {
                      label: 'Accept',
                      cyTag: 'btn-accept',
                      onClick: onAccept,
                      disabled: actionsDisabled,
                      colorVariant: 'primary' as const,
                    },
                  ]
                : []),
              ...(showCounter
                ? [
                    {
                      label: counterLabel,
                      cyTag: 'btn-counter',
                      onClick: () =>
                        onCounter(opportunity.responseId, {
                          startFirstNegotiation: latestNegotiation == null,
                        }),
                      disabled: actionsDisabled,
                      colorVariant: 'secondary' as const,
                    },
                  ]
                : []),
              ...(showWithdraw
                ? [
                    {
                      label: 'Withdraw',
                      cyTag: 'btn-withdraw',
                      onClick: onWithdraw,
                      disabled: actionsDisabled,
                      colorVariant: 'tertiary' as const,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </div>

      <div className="shrink-0 p-4 sm:p-6">
        {/* Identity access opens the lower section, below the divider, as on Job Responses and
            Provider Responses. */}
        {identityControls && <div className="mb-5">{identityControls}</div>}
        <div className="mb-5">
          <ResponseProvidersSection
            responseId={opportunity.responseId}
            providers={responseProviders}
            responseUnmasked={opportunity.isUnmasked}
            listingSide={opportunity.listingSide}
            viewerRole={opportunity.viewerRole}
          />
        </div>
        <B2BNegotiationHistory
          responseId={opportunity.responseId}
          latestNegotiation={latestNegotiation}
          negotiationSummaries={negotiationSummaries}
          viewerSender="RESPONDER"
        />
      </div>
    </MarketContentPanel>
  );
};
